import React, { useContext, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { MessageDeliveryStatus } from '@atlassian/ipm-choreographer';

import { useSessionData } from '@confluence/session-data';
import { markErrorAsHandled } from '@confluence/graphql';
import {
	ONBOARDING_NTH_USER_CONTENT_TYPE_SPOTLIGHT,
	ExperienceTrackerContext,
	ExperienceFailure,
	ExperienceSuccess,
	ExperienceStart,
} from '@confluence/experience-tracker';
import {
	GenericErrorBoundary,
	Attribution,
	ErrorDisplay,
	isUnauthorizedError,
} from '@confluence/error-boundary';
import {
	useSetOnboardingState,
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { NTH_USER_SETUP_CONTENT_TYPE } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useIsNthUserContentTypePageEnabled } from '@confluence/onboarding-helpers/entry-points/hooks/useIsNthUserContentTypePageEnabled';
import { useChoreographerAssets } from '@confluence/choreographer-services';
import { expValEquals } from '@confluence/feature-experiments';

// CHECK TRANSLATION STATUS AND USE i18n-key-rename.json
enum ConfluenceEdition {
	FREE = 'FREE',
	PREMIUM = 'PREMIUM',
	STANDARD = 'STANDARD',
}

type ContentType = 'live-doc' | 'page' | 'whiteboard' | 'database';
type Position =
	| 'top left'
	| 'top center'
	| 'top right'
	| 'right top'
	| 'right middle'
	| 'right bottom'
	| 'bottom left'
	| 'bottom center'
	| 'bottom right'
	| 'left top'
	| 'left middle'
	| 'left bottom'
	| undefined;

export type OnboardingNudgeProps = {
	contentType: ContentType;
	position?: Position;
};

function isErrorHandled(error: Parameters<typeof markErrorAsHandled>[0]) {
	// Mark unauthorized error as handled and not report to monitoring client
	if (isUnauthorizedError(error) || error?.graphQLErrors?.[0]?.message === 'unauthorized') {
		markErrorAsHandled(error);
		return true;
	}
	return false;
}

const i18n = defineMessages({
	popularHeading: {
		id: 'experiment-onboarding-content-type-spotlight.nth-user.content-types.popular.heading',
		defaultMessage: 'This is the most popular {contentType} in your site',
		description:
			'Heading for a nudge around a page, live doc, whiteboard, or database highlighting the most popular content',
	},
	nonPopularHeading: {
		id: 'experiment-onboarding-content-type-spotlight.nth-user.content-types.non-popular.heading',
		defaultMessage: 'This is a sample {contentType} in your space',
		description:
			'Heading for a nudge around a page, live doc, whiteboard, or database highlighting the most popular content',
	},
	contentDescription: {
		id: 'experiment-onboarding-content-type-spotlight.nth-user.content-types.content.description',
		defaultMessage:
			'Take a quick look at the {contentType} and learn how Confluence {pluralContentType} work.',
		description:
			'Content description for a nudge around a page, live doc, whiteboard, or database highlighting the most popular content',
	},
	pageContentType: {
		id: 'experiment-onboarding-content-type-spotlight.nth-user.content-types.page.content-type',
		defaultMessage: 'page',
		description: 'Text in a nudge describing the content type the user is looking at',
	},
	liveDocContentType: {
		id: 'experiment-onboarding-content-type-spotlight.nth-user.content-types.live-doc.content-type',
		defaultMessage: 'live doc',
		description: 'Text in a nudge describing the content type the user is looking at',
	},
	whiteboardContentType: {
		id: 'experiment-onboarding-content-type-spotlight.nth-user.content-types.whiteboard.content-type',
		defaultMessage: 'whiteboard',
		description: 'Text in a nudge describing the content type the user is looking at',
	},
	databaseContentType: {
		id: 'experiment-onboarding-content-type-spotlight.nth-user.content-types.database.content-type',
		defaultMessage: 'database',
		description: 'Text in a nudge describing the content type the user is looking at',
	},
	pluralPageContentType: {
		id: 'experiment-onboarding-content-type-spotlight.nth-user.content-types.plural.page.content-type',
		defaultMessage: 'pages',
		description:
			'Text in a nudge describing the plural version of the content type the user is looking at',
	},
	pluralLiveDocContentType: {
		id: 'experiment-onboarding-content-type-spotlight.nth-user.content-types.plural.live-doc.content-type',
		defaultMessage: 'live docs',
		description:
			'Text in a nudge describing the plural version of the content type the user is looking at',
	},
	pluralWhiteboardContentType: {
		id: 'experiment-onboarding-content-type-spotlight.nth-user.content-types.plural.whiteboard.content-type',
		defaultMessage: 'whiteboards',
		description:
			'Text in a nudge describing the plural version of the content type the user is looking at',
	},
	pluralDatabaseContentType: {
		id: 'experiment-onboarding-content-type-spotlight.nth-user.content-types.plural.database.content-type',
		defaultMessage: 'databases',
		description:
			'Text in a nudge describing the plural version of the content type the user is looking at',
	},
	dismissAction: {
		id: 'experiment-onboarding-content-type-spotlight.dismiss.action',
		defaultMessage: 'Dismiss',
		description: 'Action text for a button to dismiss a spotlight card',
	},
});

export const contentTypeMapping = (contentType: ContentType) => {
	return {
		page: {
			singular: i18n.pageContentType,
			plural: i18n.pluralPageContentType,
			target: 'onboarding-content-type-page-content-renderer',
		},
		'live-doc': {
			singular: i18n.liveDocContentType,
			plural: i18n.pluralLiveDocContentType,
			target: 'onboarding-content-type-live-doc-content-renderer',
		},
		whiteboard: {
			singular: i18n.whiteboardContentType,
			plural: i18n.pluralWhiteboardContentType,
			target: 'onboarding-content-type-whiteboard-content-renderer',
		},
		database: {
			singular: i18n.databaseContentType,
			plural: i18n.pluralDatabaseContentType,
			target: 'onboarding-content-type-database-content-renderer',
		},
	}[contentType];
};

const actionSubjectId = 'contentTypeNthUserOnboardingNudge';
const source = 'contentTypeScreen';

export const ContentTypeSpotlight = ({
	position = 'top right',
	contentType,
}: OnboardingNudgeProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const { Spotlight } = useChoreographerAssets();
	const { setOnboardingState } = useSetOnboardingState();
	const experienceTracker = useContext(ExperienceTrackerContext);

	// TODO: Potentially refactor to use state from hook
	const { showContentTypeScreen } = useIsNthUserContentTypePageEnabled();

	const [isLocalNudgeShowing, setIsLocalNudgeShowing] = useState(false);

	const {
		data: onboardingStateData,
		loading: onboardingStateLoading,
		error: onboardingStateError,
	} = useGetOnboardingState(
		[
			NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_SELECTED_CONTENT_TYPE,
			NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_CONTENT_TYPE_SPOTLIGHT_SEEN,
			NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_CONTENT_TYPE_POPULAR_CONTENT_FOUND,
		],
		// skip condition to check showContentTypeScreen (serves as ff experiment check)
		!showContentTypeScreen,
	);

	const { nthUserContentTypeSpotlightSeen, nthUserContentTypePopularContentFound } =
		deserializeState(onboardingStateData);

	const isOnboardingStateLoaded = Boolean(
		!onboardingStateLoading && !onboardingStateError && onboardingStateData,
	);
	const isEligibleForNudge =
		isOnboardingStateLoaded &&
		showContentTypeScreen &&
		!nthUserContentTypeSpotlightSeen &&
		Boolean(contentTypeMapping(contentType));

	// Uses the onboarding state to determine if the nudge should be shown
	useEffect(() => {
		// TODO: We can enforce nthUserContentType and id
		if (isEligibleForNudge) {
			setIsLocalNudgeShowing(true);
		}
	}, [
		onboardingStateData,
		onboardingStateLoading,
		onboardingStateError,
		nthUserContentTypeSpotlightSeen,
		setOnboardingState,
		createAnalyticsEvent,
		contentType,
		isEligibleForNudge,
	]);

	useEffect(() => {
		if (isLocalNudgeShowing) {
			// If the view content is too large, scroll to the top of the page
			setTimeout(() => {
				window.scrollTo({ top: 0, behavior: 'smooth' });
			}, 0);
		}
	}, [isLocalNudgeShowing, experienceTracker]);

	const handleOnDismiss = () => {
		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'dismissed',
				actionSubject: 'button',
				actionSubjectId,
				source,
				attributes: {
					contentType,
				},
			},
		}).fire();

		setIsLocalNudgeShowing(false);
	};

	const headingMessage = nthUserContentTypePopularContentFound
		? i18n.popularHeading
		: i18n.nonPopularHeading;

	if (onboardingStateError && !isErrorHandled(onboardingStateError)) {
		return (
			<>
				<ErrorDisplay error={onboardingStateError} />
				<ExperienceFailure
					name={ONBOARDING_NTH_USER_CONTENT_TYPE_SPOTLIGHT}
					error={onboardingStateError}
					attributes={{ contentType }}
				/>
			</>
		);
	}

	if (!isLocalNudgeShowing) {
		return null;
	}

	return (
		<>
			<Spotlight
				actions={[
					{
						onClick: handleOnDismiss,
						text: 'Dismiss',
					},
				]}
				heading={intl.formatMessage(headingMessage, {
					contentType: intl.formatMessage(contentTypeMapping(contentType).singular),
				})}
				targetRadius={4}
				target={contentTypeMapping(contentType).target}
				key={contentTypeMapping(contentType).target}
				dialogPlacement={position}
				dialogWidth={275}
				shouldWatchTarget
				targetBgColor={token('elevation.surface')}
				messageType="engagement"
				onMessageDisposition={(message) => {
					if (message === MessageDeliveryStatus.STARTED) {
						createAnalyticsEvent({
							type: 'sendScreenEvent',
							data: {
								action: 'viewed',
								name: actionSubjectId,
								source,
								attributes: {
									contentType,
								},
							},
						}).fire();

						setOnboardingState({
							key: NTH_USER_SETUP_CONTENT_TYPE.NTH_USER_CONTENT_TYPE_SPOTLIGHT_SEEN,
							value: 'true',
						});
					}
				}}
			>
				<FormattedMessage
					{...i18n.contentDescription}
					values={{
						contentType: intl.formatMessage(contentTypeMapping(contentType).singular),
						pluralContentType: intl.formatMessage(contentTypeMapping(contentType).plural),
					}}
				/>
			</Spotlight>
			<ExperienceSuccess name={ONBOARDING_NTH_USER_CONTENT_TYPE_SPOTLIGHT} />
		</>
	);
};

export const OnboardingNthUserContentTypeSpotlight = ({
	contentType,
	position,
}: OnboardingNudgeProps) => {
	const { edition } = useSessionData();

	const isFreeEdition = edition === ConfluenceEdition.FREE;
	const isPaidEdition = !isFreeEdition;

	const experienceTracker = useContext(ExperienceTrackerContext);
	const failedOnboardingNudge = useCallback(
		(error: Error) => {
			experienceTracker.fail({
				name: ONBOARDING_NTH_USER_CONTENT_TYPE_SPOTLIGHT,
				error,
				attributes: {
					contentType,
				},
			});
		},
		[experienceTracker, contentType],
	);

	if (
		!(
			(isFreeEdition &&
				expValEquals<string>(
					'cc_onboarding_content_type_nth_user_screen_free',
					'cohort',
					'experiment',
				)) ||
			(isPaidEdition &&
				expValEquals<string>(
					'cc_onboarding_content_type_nth_user_screen_paid',
					'cohort',
					'experiment',
				))
		)
	) {
		return null;
	}

	return (
		<GenericErrorBoundary
			attribution={Attribution.CC_ONBOARDING}
			onError={failedOnboardingNudge}
			renderOnError={() => {
				return null;
			}}
		>
			<ContentTypeSpotlight contentType={contentType} position={position} />
			<ExperienceStart name={ONBOARDING_NTH_USER_CONTENT_TYPE_SPOTLIGHT} />
		</GenericErrorBoundary>
	);
};
